/*
 This file is part of GNU Taler
 (C) 2022-2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AbsoluteTime,
  AmountJson,
  assertUnreachable,
  Duration,
  TalerFormAttributes,
  TalerExchangeApi,
  TranslatedString,
} from "@gnu-taler/taler-util";
import { useMemo, useState } from "preact/hooks";
import {
  InternationalizationAPI,
  useTranslationContext,
} from "../context/translation.js";
import { FormFieldController, UIFieldHandler } from "../forms/FormProvider.js";
import {
  FileFieldData,
  FormDesign,
  FormMetadata,
  UIFormElementConfig,
} from "../forms/forms-types.js";

type FormLeafValue =
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined
  | AmountJson
  | AbsoluteTime
  | Duration
  | readonly unknown[]
  | TalerExchangeApi.AmlState
  | FileFieldData;

export type FormFields<T> = {
  readonly [K in keyof T]-?: NonNullable<T[K]> extends FormLeafValue
    ? FormFieldController<T[K]>
    : NonNullable<T[K]> extends object
      ? FormFields<NonNullable<T[K]>>
      : FormFieldController<T[K]>;
};

export type FormFieldPath<T> = string extends keyof T
  ? string
  : {
      [K in keyof T & string]: NonNullable<T[K]> extends FormLeafValue
        ? K
        : NonNullable<T[K]> extends object
          ? K | `${K}.${FormFieldPath<NonNullable<T[K]>>}`
          : K;
    }[keyof T & string];

export type FormFieldValue<T, P extends string> = P extends keyof T
  ? T[P]
  : P extends `${infer K}.${infer R}`
    ? K extends keyof T
      ? FormFieldValue<NonNullable<T[K]>, R>
      : unknown
    : string extends keyof T
      ? T[string & keyof T]
      : unknown;

/**
 * Underlying state model for the form UI.
 */
export interface FormModel<T = any> {
  /** Logical form fields, shaped like the form result. */
  readonly fields: FormFields<T>;

  /**
   * Get a handler for an UI field based on the field identifier.
   */
  getHandlerForUiField(fieldId: string): UIFieldHandler;

  /**
   * Get every UI occurrence for an attribute, in design order.
   */
  getUiFieldsForAttribute<P extends FormFieldPath<T> & string>(
    attributeKey: P,
  ): Array<UIFieldHandler<FormFieldValue<T, P>>>;

  /**
   * Get the only UI occurrence for an attribute.
   *
   * Throws when the attribute is missing or has multiple UI occurrences.
   */
  getUiFieldForAttribute<P extends FormFieldPath<T> & string>(
    attributeKey: P,
  ): UIFieldHandler<FormFieldValue<T, P>>;

  /**
   * Check if a section of the form is hidden.
   */
  isSectionHidden(sectionName: string): boolean;
}

/**
 * Implementation of {@link FormModel}.
 */
class FormModelImpl<T> implements FormModel<T> {
  public fieldHandlers: { [x: string]: UIFieldHandler } = {};
  public hiddenSections: Set<string | number> = new Set();
  public fields = Object.create(null) as FormFields<T>;
  private attributeHandlers = new Map<string, UIFieldHandler[]>();

  getHandlerForUiField(fieldId: string): UIFieldHandler {
    return this.fieldHandlers[fieldId];
  }

  getUiFieldsForAttribute<P extends FormFieldPath<T> & string>(
    attributeKey: P,
  ): Array<UIFieldHandler<FormFieldValue<T, P>>> {
    return (this.attributeHandlers.get(attributeKey) ?? []) as Array<
      UIFieldHandler<FormFieldValue<T, P>>
    >;
  }

  getUiFieldForAttribute<P extends FormFieldPath<T> & string>(
    attributeKey: P,
  ): UIFieldHandler<FormFieldValue<T, P>> {
    const handlers = this.getUiFieldsForAttribute(attributeKey);
    if (handlers.length === 0) {
      throw Error(`no UI field for attribute path ${attributeKey}`);
    }
    if (handlers.length !== 1) {
      throw Error(
        `expected one UI field for attribute path ${attributeKey}, found ${handlers.length}`,
      );
    }
    return handlers[0]!;
  }

  isSectionHidden(sectionName: string): boolean {
    return this.hiddenSections.has(sectionName);
  }

  registerUiField(uiPath: string, handler: UIFieldHandler): void {
    this.fieldHandlers[uiPath] = handler;
    if (handler.field.name === "<none>") return;
    const handlers = this.attributeHandlers.get(handler.field.name) ?? [];
    handlers.push(handler);
    this.attributeHandlers.set(handler.field.name, handlers);
    setControllerIntoTree(this.fields, handler.field.name, handler.field);
  }
}

function isFieldController(value: unknown): value is FormFieldController {
  return (
    typeof value === "object" &&
    value !== null &&
    "name" in value &&
    "onChange" in value
  );
}

function setControllerIntoTree(
  root: object,
  attributePath: string,
  controller: FormFieldController,
): void {
  const parts = attributePath.split(".");
  let current = root as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    const existing = current[part];
    if (isFieldController(existing)) {
      throw Error(
        `form attribute path ${attributePath} conflicts with leaf ${parts.slice(0, i + 1).join(".")}`,
      );
    }
    if (existing === undefined) {
      current[part] = Object.create(null);
    } else if (typeof existing !== "object" || existing === null) {
      throw Error(`invalid form attribute path ${attributePath}`);
    }
    current = current[part] as Record<string, unknown>;
  }
  const leaf = parts[parts.length - 1]!;
  const existing = current[leaf];
  if (existing !== undefined && existing !== controller) {
    throw Error(
      `form attribute path ${attributePath} conflicts with an existing branch`,
    );
  }
  current[leaf] = controller;
}

export type FormValues<T> = {
  [K in keyof T]: NonNullable<T[K]> extends FormLeafValue
    ? T[K] | undefined
    : NonNullable<T[K]> extends object
      ? FormValues<NonNullable<T[K]>>
      : T[K] | undefined;
};

export type RecursivePartial<T> = {
  [K in keyof T]?: NonNullable<T[K]> extends FormLeafValue
    ? T[K]
    : NonNullable<T[K]> extends object
      ? RecursivePartial<NonNullable<T[K]>>
      : T[K];
};

export type ErrorAndLabel = {
  message: TranslatedString;
  label: TranslatedString;
  section: TranslatedString | undefined;
};

export type FormErrors<T> = {
  [K in keyof T]?: NonNullable<T[K]> extends FormLeafValue
    ? ErrorAndLabel
    : NonNullable<T[K]> extends object
      ? FormErrors<NonNullable<T[K]>>
      : ErrorAndLabel;
};

export type FormStatus<T> =
  | {
      status: "ok";
      result: T;
      errors: undefined;
    }
  | {
      status: "fail";
      result: RecursivePartial<T>;
      errors: FormErrors<T>;
    };

/**
 * FIMXE: Consider renaming this to FormModel and folding the current FormModel into it.
 */
export type FormState<T> = {
  design: FormDesign;
  model: FormModel<T>;
  fields: FormFields<T>;
  status: FormStatus<T>;
  update: (f: FormValues<T>) => void;
};

/**
 * Hook to instantiate a form from its metadata.
 */
export function useFormMeta<T>(
  form: FormMetadata,
  formContext: any,
  initialValue: RecursivePartial<FormValues<T>>,
): FormState<T> {
  let formDesign: FormDesign = useMemo(() => {
    if (typeof form.config === "function") {
      const config = form.config;
      return config(formContext);
    } else {
      return form.config;
    }
  }, [form, formContext]);
  return useForm(formDesign, initialValue);
}

/**
 * Hook to instantiate a form from its design.
 */
export function useForm<T>(
  design: FormDesign,
  initialValue: RecursivePartial<FormValues<T>>,
): FormState<T> {
  const { i18n } = useTranslationContext();
  const resetKey = stableFormKey(design, initialValue);
  const [storedForm, setStoredForm] = useState<{
    key: string;
    value: RecursivePartial<FormValues<T>>;
  }>({ key: resetKey, value: initialValue });
  // Callers frequently construct equivalent form objects inline.  A stable
  // structural key switches to the new initial record during the same render,
  // without erasing edits merely because object identity changed.
  const formValue =
    storedForm.key === resetKey ? storedForm.value : initialValue;
  const formUpdateHandler = (value: RecursivePartial<FormValues<T>>) => {
    setStoredForm({ key: resetKey, value });
  };

  const { model, result, errors } = constructFormHandler(
    design,
    formValue,
    formUpdateHandler,
    i18n,
  );

  const status = {
    status: errors === undefined ? "ok" : "fail",
    result,
    errors,
  } as FormStatus<T>;

  return {
    model,
    fields: model.fields,
    status,
    update: (f) => {
      formUpdateHandler(f as any);
    },
    design,
  };
}

/**
 * Use {@link path} to get the value of {@link object}.
 * Return {@link fallbackValue} if the target property is undefined
 */
export function getValueFromPath(
  object: any,
  path: string[],
  fallbackValue?: any,
): any {
  if (path.length === 0) return object;
  const [head, ...rest] = path;
  if (!head) {
    return getValueFromPath(object, rest, fallbackValue);
  }
  if (object === undefined) {
    return fallbackValue;
  }
  return getValueFromPath(object[head], rest, fallbackValue);
}

/**
 * Use $path to set the value $value into $object
 * Don't modify $object, returns a new value
 * returns undefined if the object is empty
 */
function setValueIntoPath(object: any, path: string[], value: any): any {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (!head) {
    return setValueIntoPath(object, rest, value);
  }
  if (object === undefined) {
    return undefinedIfEmpty({ [head]: setValueIntoPath({}, rest, value) });
  }
  return undefinedIfEmpty({
    ...object,
    [head]: setValueIntoPath(object[head] ?? {}, rest, value),
  });
}

export function undefinedIfEmpty<T extends object | undefined>(
  obj: T,
): T | undefined {
  if (obj === undefined) return undefined;
  return Object.keys(obj).some(
    (k) => (obj as Record<string, T>)[k] !== undefined,
  )
    ? obj
    : undefined;
}

function checkFormFieldIsValid(
  formElement: UIFormElementConfig,
  currentValue: unknown,
  i18n: InternationalizationAPI,
  secitonTitle: string | undefined,
  form: any,
): ErrorAndLabel | undefined {
  if (!("id" in formElement)) {
    return undefined;
  }

  if (formElement.required && isEmptyRequiredValue(currentValue)) {
    return {
      label: formElement.label as TranslatedString,
      message: i18n.str`required`,
      section: secitonTitle as TranslatedString,
    };
  } else if (formElement.validator) {
    try {
      const message = formElement.validator(currentValue as any, form);
      if (message !== undefined) {
        return {
          label: formElement.label as TranslatedString,
          message,
          section: secitonTitle as TranslatedString,
        };
      }
    } catch (e) {
      console.error(e);
      const message = i18n.str`Validation failed unexpectedly. Please contact the developers: ${String(
        e,
      )}`;
      console.log(message);
      return {
        label: formElement.label as TranslatedString,
        message,
        section: secitonTitle as TranslatedString,
      };
    }
  }
  return undefined;
}

function isEmptyRequiredValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (
    typeof value === "object" &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    return Object.keys(value).length === 0;
  }
  return false;
}

function stableFormKey(design: FormDesign, initialValue: unknown): string {
  const seen = new WeakSet<object>();
  return JSON.stringify([design, initialValue], (_key, value: unknown) => {
    if (typeof value === "function") return value.toString();
    if (typeof value === "bigint") return `${value}n`;
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[circular]";
      seen.add(value);
    }
    return value;
  });
}

/**
 * @param formValue Plain, unprocessed form contents.
 */
function constructFormHandler<T>(
  design: FormDesign,
  formValue: RecursivePartial<FormValues<T>>,
  onValueChange: (d: RecursivePartial<FormValues<T>>) => void,
  i18n: InternationalizationAPI,
): {
  model: FormModel<T>;
  result: FormStatus<T>;
  errors: FormErrors<T> | undefined;
} {
  const model = new FormModelImpl<T>();
  const controllers = new Map<string, FormFieldController>();
  let result = {} as FormStatus<T>;
  let errors: FormErrors<T> | undefined = undefined;

  function createFieldHandler(
    formElement: UIFormElementConfig,
    hiddenSection: boolean | undefined,
    handlerUiPath: string,
    secitonTitle: string | undefined,
  ): void {
    let field: UIFieldHandler;

    if ("id" in formElement) {
      const path = formElement.id.split(".");

      const currentValue = getValueFromPath(formValue as any, path, undefined);

      // compute prop based on state
      const hidden =
        hiddenSection ||
        formElement.hidden ||
        (formElement.hide && formElement.hide(currentValue, result));

      const currentError: ErrorAndLabel | undefined = !hidden
        ? checkFormFieldIsValid(
            formElement,
            currentValue,
            i18n,
            secitonTitle,
            formValue,
          )
        : undefined;

      if (currentError !== undefined) {
        errors = setValueIntoPath(errors, path, currentError);
      }

      let controller = controllers.get(formElement.id);
      if (!controller) {
        controller = {
          name: formElement.id,
          value: currentValue,
          onChange(newValue: unknown) {
            const updated = setValueIntoPath(formValue, path, newValue) ?? {};
            onValueChange(updated);
          },
        };
        controllers.set(formElement.id, controller);
      }

      field = {
        field: controller,
        error: currentError?.message,
        hidden,
      };
      if (!hidden) {
        result = setValueIntoPath(result, path, controller.value) ?? {};
      }
    } else {
      const hidden =
        hiddenSection ||
        formElement.hidden ||
        (formElement.hide && formElement.hide({}, result));
      field = {
        field: {
          name: "<none>",
          value: undefined,
          onChange: () => {},
        },
        hidden,
      };
    }

    model.registerUiField(handlerUiPath, field);
  }

  switch (design.type) {
    case "double-column": {
      design.sections.forEach((sec, secIndex) => {
        const hidden = sec.hide && sec.hide(result);
        if (hidden) {
          model.hiddenSections.add(`${secIndex}`);
        }

        sec.fields.forEach((f, fieldIndex) =>
          createFieldHandler(f, hidden, `${secIndex}.${fieldIndex}`, sec.title),
        );
      });
      break;
    }
    case "single-column": {
      design.fields.forEach((f, fieldIndex) =>
        createFieldHandler(f, undefined, `root.${fieldIndex}`, undefined),
      );
      break;
    }
    case "accept-tos": {
      const fields = [
        {
          name: TalerFormAttributes.DOWNLOADED_TERMS_OF_SERVICE,
          valid: (value: unknown) => value === true,
          label: i18n.str`Terms of service`,
          // Translators: Validation message shown until the user has opened and
          // successfully loaded the terms-of-service document in the reader.
          message: i18n.str`Read the terms of service before continuing.`,
        },
        {
          name: TalerFormAttributes.ACCEPTED_TERMS_OF_SERVICE,
          valid: (value: unknown) => value === design.tosVersion,
          // Translators: Validation-summary label for the checkbox that records
          // acceptance of a specific terms-of-service version.
          label: i18n.str`Terms acceptance`,
          // Translators: Validation message shown until the terms-of-service
          // acceptance checkbox is selected.
          message: i18n.str`Accept the terms of service before continuing.`,
        },
      ];
      for (const field of fields) {
        const path = [field.name];
        const currentValue = getValueFromPath(formValue, path, undefined);
        const currentError: ErrorAndLabel | undefined = field.valid(
          currentValue,
        )
          ? undefined
          : {
              label: field.label,
              message: field.message,
              section: undefined,
            };
        if (currentError) {
          errors = setValueIntoPath(errors, path, currentError);
        }
        const controller: FormFieldController = {
          name: field.name,
          value: currentValue,
          onChange: (newValue) => {
            onValueChange(setValueIntoPath(formValue, path, newValue) ?? {});
          },
        };
        const handler: UIFieldHandler = {
          field: controller,
          error: currentError?.message,
        };
        controllers.set(field.name, controller);
        model.registerUiField(`accept-tos.${field.name}`, handler);
        result = setValueIntoPath(result, path, currentValue) ?? {};
      }
      break;
    }
    default: {
      assertUnreachable(design);
    }
  }

  return { model, result, errors };
}
